import React from 'react'
import { TouchableOpacity, View } from 'react-native'

import { useRouter } from 'expo-router'

import {
  DIAMOND_EARN_REASONS,
  DIAMOND_EARN_PROMPTS,
  DIAMOND_REASON_PAYOUTS,
  DIAMOND_REASON_CATEGORIES,
  DIAMOND_CATEGORY_LABELS,
  type DiamondCategory
} from '@ppwr/convex/diamonds/catalog'

import { DS } from '@/design-system'

import { NavCard } from '@/screens/diamonds/components/NavCard'

interface HowToEarnCardProps {
  compact?: boolean
}

interface EarnItem {
  label: string
  value: string
}

interface EarnGroup {
  category: string
  active: boolean
  route?: string
  items: EarnItem[]
}

// Deep-links to the relevant mode — UI-only, doesn't belong in the shared catalog.
const ROUTE_BY_CATEGORY: Partial<Record<DiamondCategory, string>> = {
  pocket_pick: '/(root)/pocketPick',
  cth: '/(root)/cth',
  daily10: '/daily10',
  game: '/game/new',
  badge: '/badges'
}

export function HowToEarnCard({ compact = false }: HowToEarnCardProps) {
  const router = useRouter()

  const showHeader = compact == true ? true : false
  const isClickable = compact == true ? true : false
  const scrollEnabled = compact == true ? false : true

  const groupsByCategory: Partial<Record<DiamondCategory, EarnGroup>> = {}
  for (const reason of DIAMOND_EARN_REASONS) {
    if (reason === 'lounge_game_finished') continue // listed under Social below, though it's filed as 'game'
    const category = DIAMOND_REASON_CATEGORIES[reason]
    const label = DIAMOND_CATEGORY_LABELS[category]
    const group = groupsByCategory[category] ?? { category: label, active: true, route: ROUTE_BY_CATEGORY[category], items: [] }
    group.items.push({ label: DIAMOND_EARN_PROMPTS[reason], value: `+${DIAMOND_REASON_PAYOUTS[reason]}` })
    groupsByCategory[category] = group
  }

  // Active groups
  // come from the catalog (single source of truth for payouts). Inactive ones are real earn sources
  // that aren't implemented yet — move each into the catalog once its backend
  // payout exists, same as the active groups.
  const earn: EarnGroup[] = [
    groupsByCategory.streak,
    groupsByCategory.game,
    groupsByCategory.pocket_pick,
    groupsByCategory.cth,
    groupsByCategory.daily10,
    {
      category: 'Achievements & Milestones',
      active: true,
      route: ROUTE_BY_CATEGORY.badge,
      items: [
        ...(groupsByCategory.badge?.items ?? []),
        // one-shot badges pay the Platinum amount
        { label: 'Earn a one-shot badge', value: `+${DIAMOND_REASON_PAYOUTS.badge_tier_platinum}` }
        //{ label: 'Weekly Bounty Completed', value: '~+20' }
      ]
    },
    {
      category: 'Social',
      active: true,
      items: [
        { label: DIAMOND_EARN_PROMPTS.lounge_game_finished, value: `+${DIAMOND_REASON_PAYOUTS.lounge_game_finished}` }
        //{ label: '"Nice Hand" Tip Received', value: '+1' }
      ]
    }/*,
    {
      category: 'Risk & Skill',
      active: false,
      items: [
        { label: 'Decision-Quality Bonus', value: '~+3/hand' },
        { label: 'Press-Your-Luck Stake', value: '+15' },
        { label: 'Double-or-Nothing', value: '2x' }
      ]
    }*/
  ].filter((group): group is EarnGroup => group !== undefined)

  return (
    <NavCard
      title="How To Earn"
      handler={() => router.push('/diamonds/how-to-earn')}
      showHeader={showHeader}
      isClickable={isClickable}
      scrollEnabled={scrollEnabled}
    >
        { compact !== true &&
          <View style={{ gap: 32 }}>
            {earn.map((group) => (
              <View key={group.category} style={{ gap: 8 }}>
                {group.route ? (
                  <TouchableOpacity onPress={() => router.push(group.route as never)} activeOpacity={0.7}>
                    <DS.Text variant="Body Small/Medium" textAlign="center" uppercase>
                      {group.category}
                    </DS.Text>
                  </TouchableOpacity>
                ) : (
                  <DS.Text variant="Body Small/Medium" textAlign="center" uppercase>
                    {group.category}
                  </DS.Text>
                )}
                {group.items.map((item) => (
                  <View
                    key={item.label}
                    style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">{item.label}</DS.Text>
                    <DS.Text variant="Body Small/Medium">{item.value}</DS.Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        }
    </NavCard>
  )
}
